import React, { useCallback } from 'react';
import { Row, Col, Button } from 'antd';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useTypedSelector } from '@hooks';
import UserAvatar from '@components/UserAvatar';
import { useAuth } from '@hooks/useAuth';
import { LgWidth } from '@components/Responsive';
import { useResponsive } from '@hooks/useResponsive';
import { toggleUploadAssetModal } from '@redux/actions/modalAction';
import '@components/MediaViewerHeader/components/HeaderBreadcrumbs/HeaderBreadcrumbs.less';
import { useShareReview } from '@components/ShareReviewProvider';
import { setAsset, setAssetVariant } from '@redux/actions/mediaViewerAction';
import DropdownAssetVersions from '@components/MediaViewerHeader/components/DropdownAssetVersions';
import { useGetApprovalWorkflow } from '@hooks/queries/approval';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { ArrowLeftWithoutLineIcon, PlusIcon } from '@icons';

type HeaderAssetInfoProps = {
  password?: string | null;
  approvalWorkflowId?: string;
};

function HeaderAssetInfo({
  password,
  approvalWorkflowId
}: HeaderAssetInfoProps) {
  const { tourData } = useOnboardingProcess();
  const isAnnouncementTour =
    tourData?.type === 'announcement-media-viewer' ||
    tourData?.type === 'announcement-media-viewer-mobile';
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { isXsWidth } = useResponsive();
  const { user } = useAuth();
  // Optional: this component is also rendered for the approval-workflow
  // external-reviewer view, where there is no ShareReviewProvider above it.
  const shareReview = useShareReview(true);
  const { selectedVersion, assets } = useTypedSelector(
    ({ mediaViewer }) => mediaViewer
  );

  const { data: approvalWorkflow } = useGetApprovalWorkflow({
    params: {
      id: approvalWorkflowId || '',
      password
    }
  });

  const back = useCallback(() => {
    dispatch(setAsset(null));
    dispatch(setAssetVariant(null));
    searchParams.delete('assetId');
    setSearchParams(searchParams);
  }, [dispatch, searchParams, setSearchParams]);

  const onGoBackToApprovalAssetsList = useCallback(() => {
    searchParams.delete('assetId');
    searchParams.delete('reviewId');
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  return (
    <div className="media_viewer_header-asset-info">
      <Row gutter={16} wrap={false} align="middle">
        {(shareReview?.content?.assets.length || 0) > 1 && (
          <Button className="media_viewer_btn b-only-icon" onClick={back}>
            <ArrowLeftWithoutLineIcon style={{ fontSize: 32 }} />
          </Button>
        )}
        {(approvalWorkflow?.approvalWorkflow?.assets?.length || 0) > 1 && (
          <Button
            className="media_viewer_btn b-only-icon"
            onClick={onGoBackToApprovalAssetsList}
          >
            <ArrowLeftWithoutLineIcon />
          </Button>
        )}
        {user && (
          <Col>
            <UserAvatar
              isActive
              size={isXsWidth ? 32 : 'large'}
              src={user.pictureUrl || ''}
              userEmail={user.email}
              userName={user.name}
            />
          </Col>
        )}
        <LgWidth>
          {selectedVersion && (
            <Col>
              <Row gutter={4} wrap={false} align="middle">
                <Col>
                  <div className="media_viewer_header-asset-info-name">
                    {selectedVersion?.name}.{selectedVersion?.extension}
                  </div>
                </Col>
                {assets?.asset?.versions?.length === 1 &&
                !isAnnouncementTour ? (
                  <Col>
                    <div className="media_viewer_header-asset-info-version">
                      V{selectedVersion?.versionNumber}
                    </div>
                  </Col>
                ) : (
                  <Col>
                    <DropdownAssetVersions external={true} />
                  </Col>
                )}
                {!!assets?.asset?.approvalWorkflow &&
                  !!assets?.asset?.permissions.uploadNewVersion &&
                  !selectedVersion.demo && (
                    <Button
                      className="media_viewer_btn b-only-icon"
                      onClick={() => {
                        dispatch(
                          toggleUploadAssetModal({
                            visible: true,
                            newVersionFromMediaViewer: true,
                            workspaceId: assets.workspaceId
                          })
                        );
                      }}
                    >
                      <PlusIcon />
                    </Button>
                  )}
              </Row>
            </Col>
          )}
        </LgWidth>
      </Row>
    </div>
  );
}

export default HeaderAssetInfo;
